/** The share of the photo, from its bottom, whose color is taken (where the Destaque top fades into the page). */
const BOTTOM_SHARE = 0.25;

/** The size the photo is drawn at to be read (enough for an average, quick). */
const SAMPLE_SIZE = 48;

const hex = (value: number) => Math.round(value).toString(16).padStart(2, "0");

/**
 * The color of a photo's bottom part, as "#RRGGBB": the average of its last quarter, the part that fades into the
 * page under the Destaque top. Rejects when the photo can't be loaded or read (no CORS).
 */
export const photoBottomColor = (url: string): Promise<string> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = SAMPLE_SIZE;
        canvas.height = SAMPLE_SIZE;
        const context = canvas.getContext("2d");
        if (!context) {
          reject(new Error("No canvas"));
          return;
        }
        context.drawImage(image, 0, 0, SAMPLE_SIZE, SAMPLE_SIZE);
        const top = Math.floor(SAMPLE_SIZE * (1 - BOTTOM_SHARE));
        const { data } = context.getImageData(0, top, SAMPLE_SIZE, SAMPLE_SIZE - top);
        let [r, g, b, count] = [0, 0, 0, 0];
        for (let at = 0; at < data.length; at += 4) {
          r += data[at];
          g += data[at + 1];
          b += data[at + 2];
          count += 1;
        }
        resolve(`#${hex(r / count)}${hex(g / count)}${hex(b / count)}`.toUpperCase());
      } catch (error) {
        reject(error);
      }
    };
    image.onerror = () => reject(new Error("The photo couldn't be loaded"));
    // a copy of its own: the one the browser already shows (asked without CORS) can't be read from its cache
    image.src = `${url}${url.includes("?") ? "&" : "?"}keepo-color`;
  });

/** A "#RGB" / "#RRGGBB" color's relative luminance (WCAG); undefined for anything else. */
const luminance = (color?: string) => {
  const digits = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(color ?? "")?.[1];
  if (!digits) {
    return undefined;
  }
  const full = digits.length === 3 ? [...digits].map(digit => digit + digit).join("") : digits;
  const [r, g, b] = [0, 2, 4].map(at => {
    const channel = parseInt(full.slice(at, at + 2), 16) / 255;
    return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/** How two colors contrast (WCAG: 1 to 21; 4.5 reads as text); 0 when either isn't a color. */
export const contrast = (one?: string, other?: string) => {
  const [a, b] = [luminance(one), luminance(other)];
  if (a === undefined || b === undefined) {
    return 0;
  }
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
};

/** Text on a background: the one given while it reads (4.5:1), else white or near black, whichever reads better. */
export const readableOn = (background: string, text?: string, minimum = 4.5) => {
  if (contrast(background, text) >= minimum) {
    return text as string;
  }
  return contrast(background, "#FFFFFF") >= contrast(background, "#181A20") ? "#FFFFFF" : "#181A20";
};
